import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useMemo,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import { FixedSizeList } from 'react-window';
import { search as searchSection } from 'services/skill-matric-services/smSectionService';
import { search as searchArea } from 'services/skill-matric-services/smAreaService';
import { Link } from 'react-router-dom';
import AppContext from 'context/Context';
import { OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';

const TRAINEE_COL_WIDTH = 180;
const AREA_COL_WIDTH = 105;
const MIN_SECTION_HEADER_WIDTH = 140;
const ROW_HEIGHT = 56;
const LIST_HEIGHT = 600;
const SECTION_HEADER_HEIGHT = 40;
const AREA_HEADER_HEIGHT = 75;
const TOTAL_HEADER_HEIGHT = SECTION_HEADER_HEIGHT + AREA_HEADER_HEIGHT;

const SkillMatrixRow = ({ index, style, data: itemData }) => {
  const {
    rows,
    areas,
    isDark,
    totalWidth,
    bodyCellStyle,
    stripeColors,
    borderColor,
    areaWidthById
  } = itemData;
  const row = rows[index];
  const rowBg = index % 2 === 0 ? stripeColors.even : stripeColors.odd;

  return (
    <div
      style={{
        ...style,
        width: totalWidth,
        display: 'flex',
        background: rowBg,
        borderLeft: `1px solid ${borderColor}`
      }}
    >
      <div
        style={{
          ...bodyCellStyle,
          width: TRAINEE_COL_WIDTH,
          fontWeight: 'bold',
          justifyContent: 'flex-start',
          paddingLeft: '12px',
          background: rowBg,
          position: 'sticky',
          left: 0,
          zIndex: 1
        }}
      >
        {row.login}
      </div>
      {areas.map(a => {
        const levelNo = row[a.id];
        const status = row[a.id + '_status'];
        const source = row[a.id + '_source'];
        const sourceText = source == 0 ? 'Web' : 'Migrate';
        const areaId = a.id;
        const linkStr = `/hr/skill-matrix/section/skills/core-training-record?skillLevelNo=${levelNo}&traineeId=${row.trainee}&areaId=${areaId}`;

        const color =
          status === 'Inprogress'
            ? isDark
              ? '#FF6B6B'
              : '#D32F2F'
            : status === 'Complete'
            ? isDark
              ? '#66FFB3'
              : '#2E7D32'
            : isDark
            ? '#FFFFFF'
            : '#000000';

        return (
          <div
            key={a.id}
            style={{
              ...bodyCellStyle,
              width: areaWidthById[a.id] || AREA_COL_WIDTH,
              background: rowBg
            }}
          >
            <Link to={linkStr}>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`member-${index}-${areaId}`}>
                    {status && <p>Status: {status} </p>}
                    <p>Source : {sourceText}</p>
                  </Tooltip>
                }
              >
                <h6 style={{ color, fontSize: '20px' }} className="mb-0">
                  {levelNo ?? ''}
                </h6>
              </OverlayTrigger>
            </Link>
          </div>
        );
      })}
    </div>
  );
};

SkillMatrixRow.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  data: PropTypes.shape({
    rows: PropTypes.array.isRequired,
    areas: PropTypes.array.isRequired,
    isDark: PropTypes.bool,
    totalWidth: PropTypes.number.isRequired,
    bodyCellStyle: PropTypes.object.isRequired,
    stripeColors: PropTypes.shape({
      even: PropTypes.string.isRequired,
      odd: PropTypes.string.isRequired
    }).isRequired,
    borderColor: PropTypes.string.isRequired,
    areaWidthById: PropTypes.object.isRequired
  }).isRequired
};

const SkillMatrixTable = ({ data, loading = false }) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const [sections, setSections] = useState([]);
  const [areas, setAreas] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);

  const [listOuterEl, setListOuterEl] = useState(null);
  const listOuterRef = useCallback(node => {
    setListOuterEl(node);
  }, []);
  const headerScrollRef = useRef(null);

  useEffect(() => {
    const fetchColumns = async () => {
      setColumnsLoading(true);
      try {
        const sectionsRes = await searchSection({ isGetAll: true });
        const areasRes = await searchArea({ isGetAll: true });
        setSections(sectionsRes.items);
        setAreas(areasRes.items);
      } catch (err) {
        console.log(err);
      }
      setColumnsLoading(false);
    };

    fetchColumns();
  }, []);

  useEffect(() => {
    if (!listOuterEl) return;
    const onScroll = () => {
      if (headerScrollRef.current) {
        headerScrollRef.current.style.transform = `translateX(-${listOuterEl.scrollLeft}px)`;
      }
    };
    listOuterEl.addEventListener('scroll', onScroll);
    return () => listOuterEl.removeEventListener('scroll', onScroll);
  }, [listOuterEl]);

  const borderColor = isDark ? '#4a4f58' : '#c4c9d0';

  const headerCellStyle = {
    backgroundColor: isDark ? 'rgb(34 45 59)' : '#d8e2ef',
    color: isDark ? 'white' : '#000000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    lineHeight: 1.15,
    whiteSpace: 'normal',
    padding: '8px 12px',
    borderRight: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    boxSizing: 'border-box',
    flexShrink: 0
  };

  const bodyCellStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 10px',
      borderRight: `1px solid ${borderColor}`,
      borderBottom: `1px solid ${borderColor}`,
      boxSizing: 'border-box',
      flexShrink: 0,
      color: isDark ? 'white' : 'black',
      fontSize: '0.8rem'
    }),
    [isDark, borderColor]
  );

  const stripeColors = useMemo(
    () => ({
      even: isDark ? '#1e1e2f' : '#ffffff',
      odd: isDark ? '#262838' : 'aliceblue'
    }),
    [isDark]
  );

  const areasBySection = useMemo(
    () =>
      sections
        .map(s => {
          const sectionAreas = areas.filter(a => a.sectionId == s.id);
          const perAreaWidth =
            sectionAreas.length === 1
              ? Math.max(AREA_COL_WIDTH, MIN_SECTION_HEADER_WIDTH)
              : AREA_COL_WIDTH;
          return { section: s, areas: sectionAreas, perAreaWidth };
        })
        .filter(({ areas: sectionAreas }) => sectionAreas.length > 0),
    [sections, areas]
  );

  const areaWidthById = useMemo(() => {
    const map = {};
    areasBySection.forEach(({ areas: sectionAreas, perAreaWidth }) => {
      sectionAreas.forEach(a => {
        map[a.id] = perAreaWidth;
      });
    });
    return map;
  }, [areasBySection]);

  const sortedAreas = useMemo(
    () => areasBySection.flatMap(({ areas: sectionAreas }) => sectionAreas),
    [areasBySection]
  );

  const totalWidth = useMemo(
    () =>
      TRAINEE_COL_WIDTH +
      sortedAreas.reduce(
        (sum, a) => sum + (areaWidthById[a.id] || AREA_COL_WIDTH),
        0
      ),
    [sortedAreas, areaWidthById]
  );

  const rowItemData = useMemo(
    () => ({
      rows: data,
      areas: sortedAreas,
      isDark,
      totalWidth,
      bodyCellStyle,
      stripeColors,
      borderColor,
      areaWidthById
    }),
    [
      data,
      sortedAreas,
      isDark,
      totalWidth,
      bodyCellStyle,
      stripeColors,
      borderColor,
      areaWidthById
    ]
  );

  const isLoading = loading || columnsLoading;

  return (
    <div className="skill-matrix">
      {!isLoading && (
        <div style={{ display: 'flex' }}>
          <div
            style={{
              ...headerCellStyle,
              width: TRAINEE_COL_WIDTH,
              height: TOTAL_HEADER_HEIGHT,
              borderTop: `1px solid ${borderColor}`,
              borderLeft: `1px solid ${borderColor}`,
              position: 'sticky',
              left: 0,
              zIndex: 3
            }}
          >
            Trainee
          </div>
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <div
              ref={headerScrollRef}
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: totalWidth - TRAINEE_COL_WIDTH,
                borderTop: `1px solid ${borderColor}`
              }}
            >
              <div style={{ display: 'flex' }}>
                {areasBySection.map(
                  ({ section, areas: sectionAreas, perAreaWidth }) => (
                    <div
                      key={section.id}
                      style={{
                        ...headerCellStyle,
                        width: sectionAreas.length * perAreaWidth,
                        height: SECTION_HEADER_HEIGHT
                      }}
                    >
                      {section.name}
                    </div>
                  )
                )}
              </div>
              <div style={{ display: 'flex' }}>
                {sortedAreas.map(a => (
                  <div
                    key={a.id}
                    style={{
                      ...headerCellStyle,
                      width: areaWidthById[a.id] || AREA_COL_WIDTH,
                      height: AREA_HEADER_HEIGHT
                    }}
                  >
                    <div
                      style={{
                        transform: 'rotate(-45deg)',
                        textAlign: 'center',
                        width: '110px',
                        whiteSpace: 'normal'
                      }}
                    >
                      {a.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="text-center p-4">
          <Spinner variant="primary" animation="border" role="status" />
        </div>
      )}

      {!isLoading && data.length === 0 && (
        <div className="text-center p-4">No Results</div>
      )}

      {!isLoading && data.length > 0 && (
        <FixedSizeList
          height={LIST_HEIGHT}
          width="100%"
          itemCount={data.length}
          itemSize={ROW_HEIGHT}
          itemData={rowItemData}
          outerRef={listOuterRef}
        >
          {SkillMatrixRow}
        </FixedSizeList>
      )}
    </div>
  );
};

SkillMatrixTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool
};

export default SkillMatrixTable;
